import React, { useEffect, useMemo, useRef } from 'react';
import FormWrapper from '../../../BaseModule/Components/Form/FormWrapper';
import WizardText from '../v2/WizardText/WizardText';
import {
	getWizardStepDescriptionItem,
	getWizardStepTitleItem,
} from '../../Services/wizard.service';
import WizardChildren from '../WizardChildren';
import useWizardForm from '../../Hooks/useWizardForm';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { WizardRootItem } from '../../Interfaces/Wizard.interface';
import styles from './WizardRender.module.scss';

interface Props {
	activeStep: WizardRootItem;
	onSubmit: (values: object) => void;
	wizardTitleStyle?: React.CSSProperties;
	wizardDescriptionStyle?: React.CSSProperties;
}

const WizardRender: React.FC<Props> = ({
	activeStep,
	onSubmit,
	wizardTitleStyle,
	wizardDescriptionStyle,
}) => {
	const { generateWizardFormValidationScheme } = useWizardForm();
	const didInitStepRef = useRef<string | null>(null);

	const validationScheme = useMemo(() => {
		return generateWizardFormValidationScheme(activeStep);
	}, [activeStep]);

	const formMethods = useForm<Record<string, unknown>>({
		resolver: yupResolver(validationScheme),
		shouldUnregister: true,
	});

	useEffect(() => {
		const stepId = activeStep?.id ?? null;
		if (!stepId) return;

		if (didInitStepRef.current === stepId) return;
		didInitStepRef.current = stepId;

		formMethods.reset(activeStep?.settings?.defaultData ?? {});
	}, [activeStep?.id]);

	return (
		<div>
			<FormWrapper formMethods={formMethods}>
				<div
					className={styles.header}
					style={{ gap: activeStep.settings?.headerGap }}
				>
					{activeStep.title && (
						<WizardText
							item={getWizardStepTitleItem(activeStep)}
							customStyle={wizardTitleStyle}
						/>
					)}
					{activeStep.description && (
						<WizardText
							item={getWizardStepDescriptionItem(activeStep)}
							customStyle={wizardDescriptionStyle}
						/>
					)}
				</div>
				{activeStep.children.map(item => (
					<WizardChildren key={item.id} item={item} onSubmit={onSubmit} />
				))}
			</FormWrapper>
		</div>
	);
};

export default WizardRender;
